/* =====================================================================
   BrandingProvider — applies the active BrandTheme before first paint.
   BEHAVIOR-PIN: multi-tenant seam. Today it applies SLIK_THEME; the future
   swap is loading the theme from Firestore businessSettings (by shopId /
   custom domain) and passing it in as `theme`. useLayoutEffect applies the
   CSS vars before paint so there's no flash; index.css holds matching
   fallback values for the pre-hydration instant.
   See memory: project-slik-customer-portal-branding-2026-09-06.
===================================================================== */
import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { BrandTheme, SLIK_THEME, applyTheme } from './branding';
import { loadPublicTheme } from './themeRuntime';

interface BrandingContextValue {
  theme: BrandTheme;
  setTheme: (t: BrandTheme) => void;
}

const BrandingContext = createContext<BrandingContextValue>({
  theme: SLIK_THEME,
  setTheme: () => {},
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider: React.FC<{ theme?: BrandTheme; children: React.ReactNode }> = ({
  theme: initial = SLIK_THEME,
  children,
}) => {
  const [theme, setTheme] = useState<BrandTheme>(initial);

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Phase 3 (Jose 2026-09-08): pull the owner's saved theme from the public mirror
  // (publicConfig/theme) and retint the brand + set tones once it resolves. Non-blocking.
  useEffect(() => { loadPublicTheme(); }, []);

  return (
    <BrandingContext.Provider value={{ theme, setTheme }}>
      {children}
    </BrandingContext.Provider>
  );
};
